// ReviewReport.jsx — modal report with print only, no sharing
import { useRef } from 'react'

const GOAL_COLORS = ['#4F46E5','#059669','#D97706','#7C3AED','#DB2777']
const GOAL_BG     = ['#EEF2FF','#ECFDF5','#FFFBEB','#F5F3FF','#FDF2F8']
function gc(i)  { return GOAL_COLORS[i % GOAL_COLORS.length] }
function gbg(i) { return GOAL_BG[i % GOAL_BG.length] }
function scoreColor(s) { return !s ? '#94A3B8' : s >= 8 ? '#059669' : s >= 6 ? '#4F46E5' : s >= 4 ? '#D97706' : '#EF4444' }
function scoreLabel(s) { return !s ? '—' : s >= 8 ? 'Outstanding' : s >= 6 ? 'Good' : s >= 4 ? 'Average' : 'Below Average' }
function fmtDateTime(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('en-US', { day:'numeric', month:'long', year:'numeric', hour:'2-digit', minute:'2-digit' })
}

function PieChart({ goals }) {
  const total = goals.reduce((s, g) => s + (g.weightage || 0), 0)
  if (!total) return null
  const size = 160, cx = 80, cy = 80, r = 65
  let cum = -Math.PI / 2
  const slices = goals.map((g, i) => {
    const angle = (g.weightage / total) * 2 * Math.PI
    const sa = cum, ea = cum + angle; cum = ea
    const x1 = cx + r * Math.cos(sa), y1 = cy + r * Math.sin(sa)
    const x2 = cx + r * Math.cos(ea), y2 = cy + r * Math.sin(ea)
    const mid = sa + angle / 2
    return {
      d: `M${cx} ${cy} L${x1.toFixed(1)} ${y1.toFixed(1)} A${r} ${r} 0 ${angle > Math.PI ? 1 : 0} 1 ${x2.toFixed(1)} ${y2.toFixed(1)}Z`,
      lx: cx + r * 0.65 * Math.cos(mid), ly: cy + r * 0.65 * Math.sin(mid),
      show: g.weightage / total > 0.09, label: `${g.weightage}%`, color: gc(i),
    }
  })
  return (
    <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size}>
      {slices.map((s, i) => (
        <g key={i}>
          <path d={s.d} fill={s.color} stroke="#fff" strokeWidth={2.5} />
          {s.show && <text x={s.lx.toFixed(1)} y={s.ly.toFixed(1)} textAnchor="middle"
            dominantBaseline="middle" fill="#fff" fontSize={10} fontWeight="800">{s.label}</text>}
        </g>
      ))}
    </svg>
  )
}

function BarChart({ goals }) {
  const bh = 28, gap = 9, lw = 52, bw = 190, sw = 50
  const h = goals.length * (bh + gap)
  return (
    <svg viewBox={`0 0 ${lw + bw + sw} ${h}`} width={lw + bw + sw} height={h}>
      {goals.map((g, i) => {
        const y = i * (bh + gap)
        const fw = ((g.employer_score || 0) / 10) * bw
        return (
          <g key={i}>
            <text x={lw - 6} y={y + bh / 2 + 4} textAnchor="end" fontSize={10} fill="#64748B">G{g.goal_number}</text>
            <rect x={lw} y={y} width={bw} height={bh} rx={5} fill="#F1F5F9" />
            {g.employer_score > 0 && <rect x={lw} y={y} width={fw} height={bh} rx={5} fill={gc(i)} />}
            <text x={lw + bw + 6} y={y + bh / 2 + 5} fontSize={12} fontWeight="800"
              fill={g.employer_score ? scoreColor(g.employer_score) : '#94A3B8'}>
              {g.employer_score ? `${g.employer_score}/10` : '—'}
            </text>
          </g>
        )
      })}
    </svg>
  )
}

export default function ReviewReport({ review, employee, onClose }) {
  const contentRef = useRef()

  // Deduplicate by pdr_goal_id — keep latest per goal
  const entries = Object.values(
    (review.review_goal_entries || []).reduce((acc, e) => {
      const key = e.pdr_goal_id || e.goal_number
      if (!acc[key] || e.id > acc[key].id) acc[key] = e
      return acc
    }, {})
  ).sort((a, b) => a.goal_number - b.goal_number)
  const displayScore = parseFloat(review.final_score) ||
    (entries.every(e => e.employer_score)
      ? parseFloat((entries.reduce((s, e) => s + e.employer_score * (e.weightage / 100), 0)).toFixed(1))
      : null)

  function handlePrint() {
    const content = contentRef.current
    if (!content) return
    const win = window.open('', '_blank', 'width=960,height=700')
    if (!win) { alert('Please allow pop-ups to print the report.'); return }
    win.document.open()
    win.document.write(`<!DOCTYPE html><html><head>
      <meta charset="utf-8">
      <title>Performance Review — ${employee?.full_name || ''}</title>
      <style>
        * { box-sizing: border-box; }
        body { margin: 0; padding: 24px; font-family: Inter, Arial, sans-serif; background: #fff; color: #1E293B; }
        @page { margin: 12mm; size: A4; }
        @media print { body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; } }
        p, h1, h2, h3 { margin: 0; }
        svg text { font-family: Inter, Arial, sans-serif; }
      </style>
    </head><body>${content.innerHTML}</body></html>`)
    win.document.close()
    win.onload = () => { win.focus(); win.print() }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 1000, overflowY: 'auto', display: 'flex', justifyContent: 'center', padding: '2rem 1rem' }}>
      <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 860, boxShadow: '0 24px 64px rgba(0,0,0,0.35)', overflow: 'hidden', alignSelf: 'flex-start' }}>

        {/* Toolbar */}
        <div style={{ background: 'linear-gradient(135deg,#1E293B,#0F172A)', padding: '1rem 1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 38, height: 38, borderRadius: 8, background: 'linear-gradient(135deg,#4F46E5,#6366F1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ color: '#fff', fontWeight: 900, fontSize: 13 }}>EH</span>
            </div>
            <div>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', letterSpacing: '.1em' }}>EMPLOYEE HIVE</div>
              <div style={{ fontSize: 16, fontWeight: 800, color: '#fff' }}>Performance Development Review Report</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={handlePrint}
              style={{ padding: '8px 18px', background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 8, fontSize: 13, fontWeight: 600, color: '#fff', cursor: 'pointer' }}>
              🖨️ Download / Print PDF
            </button>
            <button onClick={onClose}
              style={{ width: 34, height: 34, background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 8, color: '#fff', fontSize: 20, cursor: 'pointer', lineHeight: 1 }}>×</button>
          </div>
        </div>

        {/* Report body — this is what gets printed */}
        <div ref={contentRef} style={{ padding: '2rem' }}>

          {/* Meta row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginBottom: '1.5rem', padding: '1rem', background: '#F8F9FA', borderRadius: 12, border: '1px solid #E5E7EB' }}>
            {[
              ['Employee',      employee?.full_name || '—'],
              ['Organisation',  employee?.organizations?.name || '—'],
              ['Review Period', review.review_period || `Review ${review.year || ''}`],
              ['Submitted',     fmtDateTime(review.submitted_at)],
            ].map(([l, v]) => (
              <div key={l}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 3 }}>{l}</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#1E293B' }}>{v}</div>
              </div>
            ))}
          </div>

          {/* Score banner */}
          {displayScore && (
            <div style={{ background: `${scoreColor(displayScore)}10`, border: `2px solid ${scoreColor(displayScore)}30`, borderRadius: 12, padding: '1rem 1.5rem', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: 20 }}>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 4 }}>Final Score</div>
                <div style={{ fontSize: 46, fontWeight: 900, color: scoreColor(displayScore), lineHeight: 1 }}>
                  {displayScore}<span style={{ fontSize: 18, fontWeight: 400, color: '#94A3B8' }}>/10</span>
                </div>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ background: '#E5E7EB', borderRadius: 99, height: 12, marginBottom: 6 }}>
                  <div style={{ width: `${displayScore * 10}%`, height: '100%', background: scoreColor(displayScore), borderRadius: 99 }} />
                </div>
                <div style={{ fontSize: 15, fontWeight: 800, color: scoreColor(displayScore) }}>{scoreLabel(displayScore)}</div>
              </div>
              <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#94A3B8', marginBottom: 6, textAlign: 'center' }}>Weightage</div>
                  <PieChart goals={entries} />
                </div>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#94A3B8', marginBottom: 6 }}>Scores</div>
                  <BarChart goals={entries} />
                </div>
              </div>
            </div>
          )}

          {/* Goals */}
          <div style={{ fontSize: 15, fontWeight: 800, color: '#1E293B', marginBottom: 12, paddingBottom: 8, borderBottom: '2px solid #E5E7EB' }}>
            📋 Goal-by-Goal Breakdown
            <span style={{ fontSize: 12, fontWeight: 400, color: '#94A3B8', marginLeft: 8 }}>
              ({entries.length} goal{entries.length !== 1 ? 's' : ''})
            </span>
          </div>

          {entries.map((g, i) => {
            const evLinks = (g.evidence_links || []).filter(ev => ev.url)
            return (
              <div key={i} style={{ marginBottom: 16, border: `1px solid #E5E7EB`, borderLeft: `5px solid ${gc(i)}`, borderRadius: '0 12px 12px 0', overflow: 'hidden' }}>
                {/* Header */}
                <div style={{ background: gbg(i), padding: '10px 16px', borderBottom: `1px solid ${gc(i)}22`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 30, height: 30, borderRadius: '50%', background: gc(i), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 900, color: '#fff', flexShrink: 0 }}>{g.goal_number}</div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 800, color: '#1E293B' }}>{g.goal_title || `Goal ${g.goal_number}`}</div>
                      <div style={{ fontSize: 11, color: '#64748B' }}>Goal {g.goal_number} of {entries.length}</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: gc(i), background: '#fff', padding: '3px 10px', borderRadius: 99, border: `1px solid ${gc(i)}30` }}>{g.weightage}% weight</span>
                    {g.employer_score && (
                      <div style={{ textAlign: 'center', background: `${scoreColor(g.employer_score)}10`, borderRadius: 8, padding: '4px 12px', border: `1px solid ${scoreColor(g.employer_score)}30` }}>
                        <div style={{ fontSize: 18, fontWeight: 900, color: scoreColor(g.employer_score), lineHeight: 1 }}>{g.employer_score}/10</div>
                        <div style={{ fontSize: 10, fontWeight: 600, color: scoreColor(g.employer_score) }}>{scoreLabel(g.employer_score)}</div>
                      </div>
                    )}
                  </div>
                </div>
                {/* Body */}
                <div style={{ padding: '14px 16px', background: '#fff' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 10 }}>
                    <div style={{ background: '#F8F9FA', borderRadius: 8, padding: '10px 12px' }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 5 }}>📋 What was planned</div>
                      <p style={{ fontSize: 13, color: '#475569', margin: 0, lineHeight: 1.6 }}>{g.goal_description || '—'}</p>
                      {g.kpi && <p style={{ fontSize: 12, color: '#64748B', margin: '5px 0 0' }}><strong>KPI:</strong> {g.kpi}</p>}
                    </div>
                    <div style={{ background: '#F8F9FA', borderRadius: 8, padding: '10px 12px' }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 5 }}>✅ Accomplished</div>
                      <p style={{ fontSize: 13, color: '#1E293B', margin: 0, lineHeight: 1.6 }}>{g.what_was_done || '—'}</p>
                    </div>
                  </div>
                  {evLinks.length > 0 && (
                    <div style={{ marginBottom: g.employer_comment ? 10 : 0 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 6 }}>📁 Evidence</div>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {evLinks.map((ev, ei) => (
                          <a key={ei} href={ev.url} target="_blank" rel="noreferrer"
                            onClick={e => e.stopPropagation()}
                            style={{ fontSize: 12, fontWeight: 600, color: '#4F46E5', background: '#EEF2FF', padding: '4px 12px', borderRadius: 99, textDecoration: 'none', border: '1px solid #C7D2FE' }}>
                            {ev.label || 'View evidence'}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                  {g.employer_comment && (
                    <div style={{ background: '#F0F0FF', borderRadius: 8, padding: '8px 12px' }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: '#4F46E5', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 4 }}>Manager Comment</div>
                      <p style={{ fontSize: 13, color: '#1E293B', margin: 0, lineHeight: 1.7 }}>{g.employer_comment}</p>
                    </div>
                  )}
                </div>
              </div>
            )
          })}

          {/* Manager overall */}
          {(review.employer_final_comment || review.improvement_areas) && (
            <div style={{ background: '#F0F0FF', border: '1px solid #C7D2FE', borderRadius: 12, padding: '1.25rem', marginBottom: '1.5rem' }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: '#4F46E5', marginBottom: '1rem' }}>💬 Manager's Overall Assessment</div>
              {review.employer_final_comment && (
                <div style={{ marginBottom: review.improvement_areas ? 12 : 0 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 4 }}>Overall Comment</div>
                  <p style={{ fontSize: 13, color: '#1E293B', margin: 0, lineHeight: 1.7 }}>{review.employer_final_comment}</p>
                </div>
              )}
              {review.improvement_areas && (
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#D97706', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 4 }}>Areas for Improvement</div>
                  <p style={{ fontSize: 13, color: '#1E293B', margin: 0, lineHeight: 1.7 }}>{review.improvement_areas}</p>
                </div>
              )}
            </div>
          )}

          {/* Sign-off — only show if review is signed off */}
          {(review.status === 'signed_off' || review.signed_off_at) && (
            <div style={{ background: '#ECFDF5', border: '2px solid #A7F3D0', borderRadius: 12, padding: '1.5rem', marginBottom: '1.5rem' }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: '#059669', marginBottom: '1rem' }}>✅ Official Sign-off Record</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                {/* Employee signature */}
                <div style={{ background: '#fff', borderRadius: 10, padding: '1rem', border: '1px solid #D1FAE5' }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 8 }}>Employee Acknowledgement</div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: '#1E293B', marginBottom: 4 }}>{employee?.full_name}</div>
                  <div style={{ fontSize: 11, color: '#64748B', marginBottom: 10 }}>
                    {employee?.position || employee?.email}
                  </div>
                  {review.employee_sign_off_comment && (
                    <p style={{ fontSize: 12, color: '#475569', margin: '0 0 12px', lineHeight: 1.6, fontStyle: 'italic', background: '#F0FDF4', padding: '8px 10px', borderRadius: 6, borderLeft: '3px solid #059669' }}>
                      "{review.employee_sign_off_comment}"
                    </p>
                  )}
                  {/* Signature line */}
                  <div style={{ borderTop: '2px solid #1E293B', paddingTop: 6, marginTop: 8 }}>
                    <div style={{ fontSize: 16, fontFamily: 'Georgia, serif', color: '#1E293B', fontStyle: 'italic', marginBottom: 2 }}>{employee?.full_name}</div>
                    <div style={{ fontSize: 10, color: '#94A3B8' }}>Signed: {fmtDateTime(review.signed_off_at)}</div>
                  </div>
                </div>
                {/* Employer signature */}
                <div style={{ background: '#fff', borderRadius: 10, padding: '1rem', border: '1px solid #D1FAE5' }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 8 }}>Manager / Employer Review</div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: '#1E293B', marginBottom: 4 }}>{employee?.organizations?.name || 'Manager'}</div>
                  <div style={{ fontSize: 11, color: '#64748B', marginBottom: 10 }}>Organisation</div>
                  {review.employer_final_comment && (
                    <p style={{ fontSize: 12, color: '#475569', margin: '0 0 12px', lineHeight: 1.6, fontStyle: 'italic', background: '#EFF6FF', padding: '8px 10px', borderRadius: 6, borderLeft: '3px solid #4F46E5' }}>
                      "{review.employer_final_comment}"
                    </p>
                  )}
                  {/* Signature line */}
                  <div style={{ borderTop: '2px solid #1E293B', paddingTop: 6, marginTop: 8 }}>
                    <div style={{ fontSize: 16, fontFamily: 'Georgia, serif', color: '#1E293B', fontStyle: 'italic', marginBottom: 2 }}>{employee?.organizations?.name || 'Authorised Signatory'}</div>
                    <div style={{ fontSize: 10, color: '#94A3B8' }}>Reviewed: {fmtDateTime(review.employer_reviewed_at)}</div>
                  </div>
                </div>
              </div>
            </div>
          )}
          {/* If not yet signed off — show pending notice */}
          {review.status !== 'signed_off' && !review.signed_off_at && (
            <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 12, padding: '1rem 1.25rem', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 18 }}>⏳</span>
              <div style={{ fontSize: 13, color: '#92400E' }}>Sign-off pending — this report is not yet finalised.</div>
            </div>
          )}

          {/* Footer */}
          <div style={{ borderTop: '1px solid #E5E7EB', paddingTop: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: 11, color: '#94A3B8' }}>
              Generated by <strong style={{ color: '#4F46E5' }}>Employee Hive</strong> · {fmtDateTime(new Date().toISOString())}
            </div>
            <div style={{ fontSize: 11, color: '#64748B', fontWeight: 700 }}>www.employeehive.com</div>
          </div>
        </div>
      </div>
    </div>
  )
}
